<template>
  <div class="module-nav-bar-container flex-full-height" :class="{ collapsed: isCollapsed }" data-cy="module-nav-container">
    <div class="module-nav-bar" :class="{ collapsed: isCollapsed }">
      <div class="open-sidebar-content" v-if="!isCollapsed">
        <module-nav-bar-entry
          class="sticky-entry"
          :to="{ name: 'dashboard' }"
          :active="activeModule == 'dashboard'"
          :expanded="false"
          icon-name="mdp-Dashboard"
          name="Dashboard">
          Dashboard
        </module-nav-bar-entry>

        <collapse :title="sectionText(section.text)" v-for="(section, index) in filteredSections" :key="index">
          <template v-for="(menu, mindex) in section.modules" :key="mindex">
            <module-nav-bar-entry
              :active="activeModuleCheck(activeModule, menu)"
              :name="menu.name"
              :to="menu.to"
              :expanded="false"
              :icon-name="menu.icon"
              :hasGeneralRouteForChildren="section.hasGeneralRouteForChildren ?? false"
              :tooltip="!isMenuEnabled(menu) ? $t('TradeProNextDisabled') : ''"
              v-if="showModule(menu)">
              {{ menu.text }}
            </module-nav-bar-entry>
          </template>
        </collapse>
      </div>
      <div class="closed-sidebar-content" v-else>
        <module-nav-bar-entry
          class="sticky-entry"
          :to="{ name: 'dashboard' }"
          :active="activeModule == 'dashboard'"
          expanded
          icon-name="mdp-Dashboard"
          tooltip="Dashboard"
          name="Dashboard">
          Dashboard
        </module-nav-bar-entry>
        <template v-for="(menu, index) in minimizedMenu" :key="index">
          <module-nav-bar-entry
            v-if="!menu.popupChildrenWhenMinimized && showModule(menu)"
            :active="activeModuleCheck(activeModule, menu)"
            :to="menu.to"
            :expanded="true"
            :icon-name="menu.icon"
            :name="menu.name"
            :tooltip="menu.text">
            {{ menu.text }}
          </module-nav-bar-entry>
          <module-nav-bar-entry
            v-else-if="showModule(menu) || showSection(menu)"
            to="#"
            expanded
            :icon-name="menu.icon"
            :tooltip="$t(`sectionNames.` + menu.text)"
            dropdown>
            <b-dd-header>{{ $t(`${"sectionNames." + menu.text}`) }}</b-dd-header>
            <b-dd-divider></b-dd-divider>
            <template v-for="(module, mindex) in menu.modules">
              <b-dd-item
                v-if="showModule(module)"
                :to="module.to"
                :key="mindex"
                :active="activeModule === module.name"
                :icon-name="module.icon"
                :disabled="!isMenuEnabled(module)"
                v-tooltip.right="!isMenuEnabled(module) ? $t('TradeProNextDisabled') : ''"
                :name="module.name">
                {{ module.text }}
              </b-dd-item>
            </template>
          </module-nav-bar-entry>
        </template>
        <module-nav-bar-entry
          v-if="showReference"
          :to="{ name: 'reference-guide' }"
          expanded
          icon-name="cil-terrain"
          variant="warning"
          tooltip="Theme & Icons Reference(For Development Only)">
        </module-nav-bar-entry>
      </div>
      <div class="minimize-button-container" :class="{ collapsed: isCollapsed }">
        <b-btn class="minimize-button" :class="{ collapsed: isCollapsed }" @click="collapseBar">
          <i class="mdp-ChevronLeft" v-if="!isCollapsed"></i>
          <i class="mdp-ChevronRight" v-else></i>
        </b-btn>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapState } from "vuex";
import collapse from "./ModuleNavBar/ModuleNavBarCollapse.vue";
import moduleNavBarEntry from "./ModuleNavBar/ModuleNavBarEntry.vue";
import sections from "@services/sidebarModules";
import store from "@/store";

export default {
  components: {
    collapse,
    moduleNavBarEntry
  },
  data() {
    return {
      isCollapsed: true,
      showReference: false,
      sections: []
    };
  },
  computed: {
    ...mapState(["user", "userSettings", "meta", "metadata"]),
    isSupportUser() {
      const { username } = this.user;
      return username === "support";
    },
    minimizedMenu() {
      return this.sections?.flatMap((x) => (x.popupChildrenWhenMinimized ? [x] : x.modules));
    },
    activeModule() {
      if (!this.$route?.matched?.[1]?.name) return "";
      switch (this.$route.matched[1].name) {
        case "export":
        case "import":
          return `${this.$route.params.type}-${this.$route.matched[1].name}`;
        default:
          return this.$route.matched[1].name;
      }
    },
    filteredSections() {
      return this.sections?.filter((x) => this.showSection(x)) ?? [];
    }
  },
  mounted() {
    this.showReference = process.env.NODE_ENV != "production";
    this.sections = sections;
  },
  methods: {
    ...mapActions(["saveUserSettings"]),
    activeModuleCheck(activeModule, menu) {
      return activeModule?.toLowerCase() === menu.name?.toLowerCase() || activeModule?.toLowerCase() === menu.child?.toLowerCase();
    },
    collapseBar() {
      this.isCollapsed = !this.isCollapsed;
    },
    sectionText(sectionName) {
      return this.$t(`${"sectionNames." + sectionName}`);
    },
    showSection(section) {
      if (section?.modules) {
        // Check if any module has the featureStatusKey set to true
        const hasVisibleModule = section.modules.some((x) => {
          return x.to && (this.metadata.config.feature?.[x.featureStatusKey] ?? true);
        });

        return hasVisibleModule; // Return true if at least one module has a visible feature, else false
      }

      // If no modules are found, hide the section
      return false;
    },
    showModule(module) {
      return (module?.to || this.isSupportUser) && (this.metadata.config.feature?.[module.featureStatusKey] ?? true);
    },
    isMenuEnabled(module) {
      if (module.to && module.to?.params && module.to?.params?.type == "TradeProNext") {
        return store.state.globalParameters.IsTradeProNextEnabled;
      }
      return true;
    }
  },
  watch: {
    userSettings: {
      immediate: true,
      deep: true,
      handler(nv) {
        if (nv) {
          this.isCollapsed = nv.IsSidebarOpen !== "true" && nv.IsSidebarOpen !== true;
        }
      }
    },
    isCollapsed(nv) {
      if (this._previousRequest) window.clearTimeout(this._previousRequest);

      this._previousRequest = setTimeout(async () => {
        await this.saveUserSettings({ IsSidebarOpen: !nv });
        this._previousRequest = null;
      }, 500);
    }
  }
};
</script>
